import { Candle, PatternPoint, PredictionLine } from '../types';

/**
 * 计算预测线
 * @param type 分型类型
 * @param a 最高点
 * @param b 最低点
 * @returns 预测线数组
 */
export function calculatePredictionLines(
  patternType: 'top' | 'bottom',
  aPoint: number,  // A点价格 (顶分型的最高点或底分型的最低点)
  bPoint: number   // B点价格 (顶分型的最低点或底分型的最高点)
): PredictionLine[] {
  // 预测线系数
  const coefficients = [
    { number: 1, value: 0.83 },
    { number: 2, value: 1.39 },
    { number: 3, value: 2.1 },
    { number: 4, value: 2.5 },
    { number: 5, value: 2.97 },
    { number: 6, value: 3.7 },
    { number: 7, value: 4.25 },
    { number: 8, value: 5 },
    { number: 9, value: 5.8 }
  ];
  
  // 价格差异
  const priceDiff = Math.abs(aPoint - bPoint);
  
  // 根据分型类型计算预测线
  return coefficients.map(coef => {
    let lineValue: number;
    
    if (patternType === 'bottom') {
      // 底分型: A是最低点，B是最高点，预测线 = B + 系数 * (B - A)
      // 检查B是否大于A以确保计算正确
      if (bPoint <= aPoint) {
        // console.error('底分型计算错误: B点应高于A点!', { aPoint, bPoint });
      }
      lineValue = bPoint + coef.value * priceDiff;
      // console.log(`底分型计算: ${bPoint} + ${coef.value} * ${priceDiff} = ${lineValue}`);
    } else {
      // 顶分型: A是最高点，B是最低点，预测线 = B - 系数 * (A - B)
      // 检查A是否大于B以确保计算正确
      if (aPoint <= bPoint) {
        // console.error('顶分型计算错误: A点应高于B点!', { aPoint, bPoint });
      }
      lineValue = bPoint - coef.value * priceDiff;
      // console.log(`顶分型计算: ${bPoint} - ${coef.value} * ${priceDiff} = ${lineValue}`);
    }
    
    return {
      lineNumber: coef.number,
      value: lineValue,
      coefficient: coef.value,
      reached: false
    };
  });
}

/**
 * 检测顶底分型
 * @param candles K线数组
 * @returns 识别出的分型点
 */
export const detectPatterns = (candles: Candle[]): PatternPoint[] => {
  if (candles.length < 3) return []; // 至少需要3根K线才能形成分型
  
  const patterns: PatternPoint[] = [];
  
  // 从第一根K线开始检查，找到可能的X线
  for (let i = 0; i < candles.length - 2; i++) {
    // 尝试找顶分型
    tryFindTopPattern(candles, i, patterns);
    
    // 尝试找底分型
    tryFindBottomPattern(candles, i, patterns);
  }
  
  return patterns;
};

/**
 * 尝试找顶分型
 * @param candles K线数组
 * @param startIndex 开始索引
 * @param patterns 分型数组(用于存储结果)
 */
const tryFindTopPattern = (candles: Candle[], startIndex: number, patterns: PatternPoint[]): void => {
  const xCandle = candles[startIndex]; // X线
  
  // 向后查找Y线
  for (let j = startIndex + 1; j < candles.length; j++) {
    const yCandle = candles[j];
    
    // Y线收盘价低于X线最低价，顶分型成立
    if (yCandle.close < xCandle.low) {
      // X线和Y线之间的最高价和最低价
      const highest = Math.max(xCandle.high, ...candles.slice(startIndex + 1, j).map(c => c.high));
      const lowest = Math.min(xCandle.low, ...candles.slice(startIndex + 1, j).map(c => c.low));
      
      // 检查是否有Z线
      let zIndex = null;
      let zCandle = null;
      
      for (let k = j + 1; k < candles.length; k++) {
        const potentialZCandle = candles[k];
        
        // Z线要回到X线的范围内（价格反弹到X线的范围内）
        if (potentialZCandle.high >= xCandle.low && potentialZCandle.low <= xCandle.high) {
          zIndex = k;
          zCandle = potentialZCandle;
          break;
        }
      }
      
      // 创建分型点
      const patternPoint: PatternPoint = {
        type: 'top',
        xIndex: startIndex,
        yIndex: j,
        zIndex,
        xCandle,
        yCandle,
        zCandle,
        highest,
        lowest,
        predictionLines: calculatePredictionLines('top', highest, lowest)
      };
      
      patterns.push(patternPoint);
      break; // 找到一个Y线后就退出
    }
  }
};

/**
 * 尝试找底分型
 * @param candles K线数组
 * @param startIndex 开始索引
 * @param patterns 分型数组(用于存储结果)
 */
const tryFindBottomPattern = (candles: Candle[], startIndex: number, patterns: PatternPoint[]): void => {
  const xCandle = candles[startIndex]; // X线
  
  // 向后查找Y线
  for (let j = startIndex + 1; j < candles.length; j++) {
    const yCandle = candles[j];
    
    // Y线收盘价高于X线最高价，底分型成立
    if (yCandle.close > xCandle.high) {
      // X线和Y线之间的最高价和最低价
      const highest = Math.max(xCandle.high, ...candles.slice(startIndex + 1, j).map(c => c.high));
      const lowest = Math.min(xCandle.low, ...candles.slice(startIndex + 1, j).map(c => c.low));
      
      // 检查是否有Z线
      let zIndex = null;
      let zCandle = null;
      
      for (let k = j + 1; k < candles.length; k++) {
        const potentialZCandle = candles[k];
        
        // Z线要回到X线的范围内（价格反弹到X线的范围内）
        if (potentialZCandle.high >= xCandle.low && potentialZCandle.low <= xCandle.high) {
          zIndex = k;
          zCandle = potentialZCandle;
          break;
        }
      }
      
      // 创建分型点
      const patternPoint: PatternPoint = {
        type: 'bottom',
        xIndex: startIndex,
        yIndex: j,
        zIndex,
        xCandle,
        yCandle,
        zCandle,
        highest,
        lowest,
        predictionLines: calculatePredictionLines('bottom', highest, lowest)
      };
      
      patterns.push(patternPoint);
      break; // 找到一个Y线后就退出
    }
  }
};

/**
 * 检查预测线是否已到达
 * @param pattern 分型
 * @param currentPrice 当前价格
 * @returns 更新后的分型
 */
export const checkPredictionLinesReached = (pattern: PatternPoint, currentPrice: number): PatternPoint => {
  const updatedLines = pattern.predictionLines.map(line => {
    let reached = line.reached;
    
    if (pattern.type === 'top') {
      // 顶分型：价格下跌到预测线以下
      if (currentPrice <= line.value) {
        reached = true;
      }
    } else {
      // 底分型：价格上涨到预测线以上
      if (currentPrice >= line.value) {
        reached = true;
      }
    }
    
    return { ...line, reached };
  });
  
  return { ...pattern, predictionLines: updatedLines };
}; 